import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, withCtx as _withCtx, withModifiers as _withModifiers, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "col-form-label fw-bold mb-2" }
const _hoisted_2 = { class: "employee-form" }
const _hoisted_3 = {
  key: 1,
  class: "col-form-label d-block text-nowrap"
}
const _hoisted_4 = {
  key: 0,
  class: "form-text ms-1"
}
const _hoisted_5 = {
  key: 0,
  class: "border border-secondary rounded p-2 mt-2",
  "aria-describedby": "coOptionalFieldsNote"
}
const _hoisted_6 = { class: "employee-form" }
const _hoisted_7 = { class: "mb-3 row" }
const _hoisted_8 = { class: "col-sm-4" }
const _hoisted_9 = {
  key: 1,
  class: "btn-group",
  role: "group"
}
const _hoisted_10 = {
  class: "dropdown-menu",
  "aria-labelledby": "btnGroupDrop1"
}
const _hoisted_11 = ["onClick"]
const _hoisted_12 = ["onClick"]
const _hoisted_13 = ["onClick"]

import {computed, onMounted, ref} from "vue";
import {BTableColumn} from "./BTable/BTable.types";
import {FormModal, ConfirmModal, SystemAlerts} from "./Misc";
import BGrid from "./BGrid/BGrid.vue";
import type {AgencyStaffing, EmployeeData} from "@/types";
import {useFormValidation} from "@/validation";
import {number, object, string} from "yup";
import {BInput, BSelect} from "./Fields";
import {BButton} from "./BButton";
import {BAlert} from "./BAlert";
import {useConfirmDialog} from "@/composable/useConfirmDialog";
import {useEmployeeDataStore} from "@/stores/employeeDataStore";
import {storeToRefs} from "pinia";
import { handleErrorInResponse } from "@/utils";


export default /*@__PURE__*/_defineComponent({
  __name: 'EmployeeData',
  props: {
    title: { default: '' },
    stateCode: { default: '' }
  },
  setup(__props: any) {

const props = __props;

const employeeDataStore = useEmployeeDataStore();
const {loading, getDetailsForCurrentDataset} = storeToRefs(employeeDataStore);
const {confirm} = useConfirmDialog();
const {handleSubmit, errors, resetValidation} = useFormValidation();

const isShowEditEditor = ref(false);
const isEditMode = ref(false);
const selectedOri = ref("");
const oriOptions = ref<{value: string; text: string}[]>([]);
// The "no agencies" error is only valid once the ori request has actually returned.
const oriListLoaded = ref(false);
const record = ref<EmployeeData>({} as EmployeeData);
const currentYear = new Date().getFullYear();
// Year bounds for the Add dialog, taken from the agency's PE start/end dates (never beyond the current year
// or the 25 year look-back the schema allows).
const minYear = ref(currentYear - 25);
const maxYear = ref(currentYear);
const hasYearChoice = computed(() => minYear.value < maxYear.value);

const columns= ref<BTableColumn[]>([
    {
        key: "",
        text: "",
        sortable: false,
        columnClass: ["align-top text-center"],
        class: (item) => ["text-nowrap"],
        children:[
            {
                key: "year",
                text: "Year",
                sortable: false,
                columnClass: ["align-top text-center"],
                class: (item) => ["text-nowrap","text-center"],
                value: (item) => item.year,
            }
        ],
        subRows:(item) => [item],
    },
    {
        key: "fullTimeOfficers",
        text: "Full-time Enforcement Officers",
        sortable: false,
        columnClass: ["align-top text-center"],
        class: (item) => ["text-nowrap","text-center"],
        children:[
            {
                key: "sworn_in_male",
                text: "Male",
                sortable: false,
                columnClass: ["align-top text-center"],
                class: (item) => ["text-nowrap","text-center"],
                value: (item) => item.sworn_in_male,
            },
            {
                key: "sworn_in_female",
                text: "Female",
                sortable: false,
                columnClass: ["align-top text-center"],
                class: (item) => ["text-nowrap","text-center"],
                value: (item) => item.sworn_in_female,
            }
        ]
    },
    {
        key: "fullTimeCivilians",
        text: "Full-time Civilians",
        sortable: false,
        columnClass: ["align-top text-center"],
        class: (item) => ["text-nowrap","text-center"],
        children:[
            {
                key: "civilian_male",
                text: "Civilian - Male",
                sortable: false,
                columnClass: ["align-top text-center"],
                class: (item) => ["text-nowrap","text-center"],
                value: (item) => item.civilian_male,
            },
            {
                key: "civilian_female",
                text: "Civilian - Female",
                sortable: false,
                columnClass: ["align-top text-center"],
                class: (item) => ["text-nowrap","text-center"],
                value: (item) => item.civilian_female,
                
            }
        ]
    }
]);

const agencyLabel = computed(() => {
    return oriOptions.value.find((o) => o.value == selectedOri.value)?.text ?? selectedOri.value;
});

const editDialogTitle = computed(() => {
    return isEditMode.value ? `Edit Police Employee Data for ${record.value.year}` : "Add New Police Employee Data";
});

const schema = object<EmployeeData>({
    year: number()
        .transform((value) => (Number.isNaN(value) ? undefined : value))
        .integer()
        .test("year-min", () => `Year must be ${minYear.value} or later`, (value) => value == null || value >= minYear.value)
        .test("year-max", () => `Year must be ${maxYear.value} or earlier`, (value) => value == null || value <= maxYear.value)
        .required()
        .label("Year"),
    sworn_in_male: number()
        .transform((value) => (Number.isNaN(value) ? undefined : value))
        .integer()
        .min(0)
        .max(99999)
        .required()
        .label("Full-Time Sworn - Male"),
    sworn_in_female: number()
        .transform((value) => (Number.isNaN(value) ? undefined : value))
        .integer()
        .min(0)
        .max(99999)
        .required()
        .label("Full-Time Sworn - Female"),
    civilian_male: number()
        .transform((value) => (Number.isNaN(value) ? undefined : value))
        .integer()
        .min(0)
        .max(99999)
        .required()
        .label("Full-Time Civilian - Male"),
    civilian_female: number()
        .transform((value) => (Number.isNaN(value) ? undefined : value))
        .integer()
        .min(0)
        .max(99999)
        .required()
        .label("Full-Time Civilian - Female"),
}).test("total-under-max", function (value) {
    const total =
        (value.sworn_in_male ?? 0) + (value.sworn_in_female ?? 0) + (value.civilian_male ?? 0) + (value.civilian_female ?? 0);

    if (total < 100000) {
        return true;
    }

    return this.createError({
        path: "total",
        message: "The total of all full-time officers and employees must be less than 100,000.",
    });
});

if(props.stateCode == "CO"){
     columns.value.push({
        key: "",
        text: "Budgeted for next year",
        sortable: false,
        columnClass: ["align-top text-center"],
        class: (item) => ["text-nowrap"],
        children: [
            {
                key: "certified_officers_count",
                text: "Certified Officers",
                sortable: false,
                columnClass: ["align-top text-center"],
                class: (item) => ["text-nowrap","text-center"],
                value: (item) => item.certified_officers ?? "",
            },
            {
                key: "non_certified_officers_count",
                text: "Non-Certified Officers",
                sortable: false,
                columnClass: ["align-top text-center"],
                class: (item) => ["text-nowrap","text-center"],
                value: (item) => item.non_certified_officers ?? "",
            },
            {
                key: "lea_budget",
                text: "LEA Budget",
                sortable: false,
                columnClass: ["align-top text-center"],
                class: (item) => ["text-nowrap","text-center"],
                value: (item) => formatBudget(item.lea_budget),
            }
        ]});

        schema.shape.certified_officers = string()
        .trim()
        .matches(/^\d{0,5}$/, "Certified Officers must be a whole number using digits only (maximum 5 digits), or left blank.")
        .label("Certified Officers");

        schema.shape.non_certified_officers = string()
        .trim()
        .matches(/^\d{0,5}$/, "Non-Certified Officers must be a whole number using digits only (maximum 5 digits), or left blank.")
        .label("Non-Certified Officers");

        schema.shape.lea_budget = string()
        .trim()
        .matches(/^\d{0,9}$/, "LEA Budget must be a whole dollar amount of no more than $999,999,999, or left blank.")
        .label("LEA Budget");

}

columns.value.push({
        key: "actionGroup",
        text: "",
        sortable: false,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap"],
        children:[
            {
                key: "edit",
                text: "",
                sortable: false,
                columnClass: ["align-top"],
                class: (item) => ["text-nowrap"],
            },
            {
                key: "delete",
                text: "",
                sortable: false,
                columnClass: ["align-top"],
                class: (item) => ["text-nowrap"],
            },
            {
                key: "document",
                text: "",
                sortable: false,
                columnClass: ["align-top"],
                class: (item) => ["text-nowrap"],
            }
        ]
    });

const loadOris = async () => {
    const data = await employeeDataStore.getOriListForDataset();
    oriOptions.value = (data ?? []).map((o) => ({value: o.ori, text: `${o.ori} - ${o.label}`}));
    oriListLoaded.value = true;

    if (!selectedOri.value && oriOptions.value.length > 0) {
        selectedOri.value = oriOptions.value[0].value;
    }
};

// Add/edit/delete update the grid optimistically (see employeeDataStore) instead of reloading from the
// server, since the actual write goes through an async processing queue - reloading right after would
// just show stale data. loadGridData is only needed for the initial load and when switching agencies.
const loadGridData = async () => {
    try{
        if (selectedOri.value) {
            await employeeDataStore.list(selectedOri.value);
        }
    }
    catch(error) {
        handleErrorInResponse(error);
    }
};

// Dates arrive as ISO strings; read the year straight from the text so time zones can't shift it.
const yearOf = (value?: string | Date | null): number | null => {
    const year = value ? parseInt(value.toString().substring(0, 4), 10) : NaN;
    return Number.isNaN(year) ? null : year;
};

const getAgencyDatasetDetails = async () => {
    minYear.value = currentYear - 25;
    maxYear.value = currentYear;

    if (!selectedOri.value) {
        return;
    }

    try {
        await employeeDataStore.fetchDatasetDetailsForOri(selectedOri.value);
        const details = getDetailsForCurrentDataset.value;
        const startYear = yearOf(details.agency_start_date) ?? yearOf(details.start_date);
        const endYear = yearOf(details.agency_end_date) ?? yearOf(details.end_date);

        if (startYear) {
            minYear.value = Math.max(minYear.value, startYear);
        }
        if (endYear) {
            maxYear.value = Math.min(maxYear.value, endYear);
        }
    }
    catch(error) {
        handleErrorInResponse(error);
    }
};

const handleOriChange = async () => {
    await loadGridData();
    await getAgencyDatasetDetails();
};

const showAddEditor = () => {
    isEditMode.value = false;
    resetValidation();
    record.value = {
        ori: selectedOri.value,
        year: maxYear.value,
        sworn_in_male: 0,
        sworn_in_female: 0,
        civilian_male: 0,
        civilian_female: 0,
        certified_officers: "",
        non_certified_officers: "",
        lea_budget: "",
    };
    isShowEditEditor.value = true;
};

const showEditEditor = (item: AgencyStaffing) => {
    isEditMode.value = true;
    resetValidation();
    record.value = {
        ori: selectedOri.value,
        year: item.year,
        sworn_in_male: item.sworn_in_male,
        sworn_in_female: item.sworn_in_female,
        civilian_male: item.civilian_male,
        civilian_female: item.civilian_female,
        certified_officers: item.certified_officers?.toString() ?? "",
        non_certified_officers: item.non_certified_officers?.toString() ?? "",
        lea_budget: item.lea_budget?.toString() ?? "",
    };
    isShowEditEditor.value = true;
};

// record.lea_budget always holds bare digits (what the validator and the back-end see); the "$###,###,###"
// formatting only exists on the way in and out of the input and in the grid.
const cleanBudget = (value: string | number | null | undefined) => (value ?? "").toString().replace(/\D/g, "");

const formatBudget = (value: string | number | null | undefined) => {
    const digits = cleanBudget(value);
    return digits === "" ? "" : `$${Number(digits).toLocaleString("en-US")}`;
};

const leaBudgetDisplay = computed({
    get: () => formatBudget(record.value.lea_budget),
    set: (value: string) => {
        record.value.lea_budget = cleanBudget(value).slice(0, 9);
    },
});

const saveRecord = async () => {
    record.value.lea_budget = cleanBudget(record.value.lea_budget);
    if (await handleSubmit(schema, record.value)) {
        if (isEditMode.value) {
            await employeeDataStore.update(props.stateCode, record.value);
        } else {
            await employeeDataStore.create(props.stateCode, record.value);
        }

        isShowEditEditor.value = false;
    }
};

const deleteRecord = async (item: AgencyStaffing) => {
    const shouldProceed = await confirm("Delete", "Are You Sure?");
    if (shouldProceed) {
        await employeeDataStore.deleteItem(props.stateCode, selectedOri.value, item.year);
    }
};

const fileInputRef = ref<HTMLInputElement | null>(null);
const pendingUploadItem = ref<AgencyStaffing | null>(null);

const uploadDocument = (item: AgencyStaffing) => {
    pendingUploadItem.value = item;
    fileInputRef.value?.click();
};

const handleFileSelected = async (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const item = pendingUploadItem.value;
    input.value = "";
    pendingUploadItem.value = null;

    if (!file || !item) {
        return;
    }

    item.file_id = await employeeDataStore.uploadDocument(selectedOri.value, item.year, file);
};

const viewDocument = (item: AgencyStaffing) => {
    if (!item.file_id) {
        return;
    }
    window.open(`api/v1/police-employee/documents/${item.file_id}`, "_blank");
};

const deleteDocument = async (item: AgencyStaffing) => {
    const shouldProceed = await confirm("Delete", "Are You Sure?");
    if (shouldProceed) {
        await employeeDataStore.deleteDocument(selectedOri.value, item.year);
        item.file_id = null;
    }
};

onMounted(async () => {
    await loadOris();
    await Promise.all([loadGridData(), getAgencyDatasetDetails()]);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(ConfirmModal)),
    _createVNode(_unref(FormModal), {
      title: editDialogTitle.value,
      modelValue: isShowEditEditor.value,
      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((isShowEditEditor).value = $event)),
      loading: _unref(loading),
      width: 600,
      onSubmit: saveRecord
    }, {
      default: _withCtx(() => [
        _createElementVNode("fieldset", null, [
          _createElementVNode("div", _hoisted_1, _toDisplayString(agencyLabel.value), 1),
          _createElementVNode("div", _hoisted_2, [
            _cache[10] || (_cache[10] = _createElementVNode("label", {
              for: "year",
              class: "col-form-label"
            }, " Year: ", -1)),
            _createElementVNode("div", null, [
              (!isEditMode.value && hasYearChoice.value)
                ? (_openBlock(), _createBlock(_unref(BInput), {
                    key: 0,
                    name: "year",
                    type: "number",
                    "max-length": 4,
                    min: minYear.value,
                    max: maxYear.value,
                    modelValue: record.value.year,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((record.value.year) = $event)),
                    error: _unref(errors).year
                  }, null, 8, ["min", "max", "modelValue", "error"]))
                : (_openBlock(), _createElementBlock("span", _hoisted_3, [
                    _createTextVNode(_toDisplayString(record.value.year) + " ", 1),
                    (!isEditMode.value)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_4, " Only " + _toDisplayString(record.value.year) + " is available for this agency. ", 1))
                      : _createCommentVNode("", true)
                  ]))
            ]),
            _cache[11] || (_cache[11] = _createElementVNode("label", {
              for: "swornInMale",
              class: "col-form-label"
            }, " Full-Time Sworn - Male: ", -1)),
            _createElementVNode("div", null, [
              _createVNode(_unref(BInput), {
                name: "swornInMale",
                type: "number",
                min: 0,
                "max-length": 5,
                modelValue: record.value.sworn_in_male,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((record.value.sworn_in_male) = $event)),
                error: _unref(errors).sworn_in_male
              }, null, 8, ["modelValue", "error"])
            ]),
            _cache[12] || (_cache[12] = _createElementVNode("label", {
              for: "swornInFemale",
              class: "col-form-label"
            }, " Full-Time Sworn - Female: ", -1)),
            _createElementVNode("div", null, [
              _createVNode(_unref(BInput), {
                name: "swornInFemale",
                type: "number",
                min: 0,
                "max-length": 5,
                modelValue: record.value.sworn_in_female,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((record.value.sworn_in_female) = $event)),
                error: _unref(errors).sworn_in_female
              }, null, 8, ["modelValue", "error"])
            ]),
            _cache[13] || (_cache[13] = _createElementVNode("label", {
              for: "civilianMale",
              class: "col-form-label"
            }, " Full-Time Civilian - Male: ", -1)),
            _createElementVNode("div", null, [
              _createVNode(_unref(BInput), {
                name: "civilianMale",
                type: "number",
                min: 0,
                "max-length": 5,
                modelValue: record.value.civilian_male,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((record.value.civilian_male) = $event)),
                error: _unref(errors).civilian_male
              }, null, 8, ["modelValue", "error"])
            ]),
            _cache[14] || (_cache[14] = _createElementVNode("label", {
              for: "civilianFemale",
              class: "col-form-label"
            }, " Full-Time Civilian - Female: ", -1)),
            _createElementVNode("div", null, [
              _createVNode(_unref(BInput), {
                name: "civilianFemale",
                type: "number",
                min: 0,
                "max-length": 5,
                modelValue: record.value.civilian_female,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((record.value.civilian_female) = $event)),
                error: _unref(errors).civilian_female
              }, null, 8, ["modelValue", "error"])
            ])
          ]),
          (props.stateCode == 'CO')
            ? (_openBlock(), _createElementBlock("fieldset", _hoisted_5, [
                _cache[18] || (_cache[18] = _createElementVNode("legend", { class: "visually-hidden" }, "Proposition 130 eligibility fields", -1)),
                _cache[19] || (_cache[19] = _createElementVNode("p", {
                  id: "coOptionalFieldsNote",
                  class: "ms-0 mt-0 me-0 mb-2 callout callout-info"
                }, "The following fields are for eligibility for Proposition 130 law enforcement training funds and should only be completed by municipal police departments, sheriffs offices, marshals offices and municipal courts. All other agencies should leave them blank.", -1)),
                _createElementVNode("div", _hoisted_6, [
                  _cache[15] || (_cache[15] = _createElementVNode("label", {
                    for: "certifiedOfficers",
                    class: "col-form-label"
                  }, " Certified Officers: ", -1)),
                  _createElementVNode("div", null, [
                    _createVNode(_unref(BInput), {
                      name: "certifiedOfficers",
                      type: "text",
                      inputmode: "numeric",
                      pattern: "[0-9]*",
                      autocomplete: "off",
                      "max-length": 5,
                      modelValue: record.value.certified_officers,
                      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((record.value.certified_officers) = $event)),
                      error: _unref(errors).certified_officers
                    }, null, 8, ["modelValue", "error"])
                  ]),
                  _cache[16] || (_cache[16] = _createElementVNode("label", {
                    for: "nonCertifiedOfficers",
                    class: "col-form-label"
                  }, " Non-Certified Officers: ", -1)),
                  _createElementVNode("div", null, [
                    _createVNode(_unref(BInput), {
                      name: "nonCertifiedOfficers",
                      type: "text",
                      inputmode: "numeric",
                      pattern: "[0-9]*",
                      autocomplete: "off",
                      "max-length": 5,
                      modelValue: record.value.non_certified_officers,
                      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((record.value.non_certified_officers) = $event)),
                      error: _unref(errors).non_certified_officers
                    }, null, 8, ["modelValue", "error"])
                  ]),
                  _cache[17] || (_cache[17] = _createElementVNode("label", {
                    for: "leaBudget",
                    class: "col-form-label"
                  }, " LEA Budget: ", -1)),
                  _createElementVNode("div", null, [
                    _createVNode(_unref(BInput), {
                      name: "leaBudget",
                      type: "text",
                      inputmode: "numeric",
                      autocomplete: "off",
                      "max-length": 12,
                      modelValue: leaBudgetDisplay.value,
                      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((leaBudgetDisplay).value = $event)),
                      error: _unref(errors).lea_budget
                    }, null, 8, ["modelValue", "error"])
                  ])
                ])
              ]))
            : _createCommentVNode("", true),
          (_unref(errors).total)
            ? (_openBlock(), _createBlock(_unref(BAlert), {
                key: 1,
                class: "mt-3",
                variant: "danger"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(errors).total), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }, 8, ["title", "modelValue", "loading"]),
    _createElementVNode("div", _hoisted_7, [
      _createElementVNode("div", _hoisted_8, [
        _cache[20] || (_cache[20] = _createElementVNode("label", {
          for: "agency",
          class: "form-label"
        }, " Agency: ", -1)),
        _createVNode(_unref(BSelect), {
          name: "agency",
          modelValue: selectedOri.value,
          "onUpdate:modelValue": [
            _cache[9] || (_cache[9] = ($event: any) => ((selectedOri).value = $event)),
            handleOriChange
          ],
          items: oriOptions.value
        }, null, 8, ["modelValue", "items"])
      ])
    ]),
    (oriListLoaded.value && oriOptions.value.length == 0)
      ? (_openBlock(), _createBlock(_unref(BAlert), {
          key: 0,
          variant: "danger"
        }, {
          default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
            _createTextVNode(" Error, no agencies submitting PE data have been found. Please contact your administrator. ", -1)
          ]))]),
          _: 1
        }))
      : (_openBlock(), _createBlock(BGrid, {
          key: 1,
          caption: "Employee Data",
          items: _unref(employeeDataStore).items,
          columns: columns.value,
          "show-filter": false,
          "show-paging": false,
          loading: _unref(loading)
        }, {
          beforeFilter: _withCtx(() => [
            _createVNode(_unref(BButton), {
              onClick: _withModifiers(showAddEditor, ["prevent"])
            }, {
              default: _withCtx(() => [...(_cache[22] || (_cache[22] = [
                _createTextVNode("Add Record", -1)
              ]))]),
              _: 1
            })
          ]),
          "cell.edit": _withCtx(({item}) => [
            _createVNode(_unref(BButton), {
              onClick: _withModifiers(($event: any) => (showEditEditor(item)), ["prevent"]),
              size: "sm"
            }, {
              default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                _createTextVNode(" Edit ", -1)
              ]))]),
              _: 1
            }, 8, ["onClick"])
          ]),
          "cell.delete": _withCtx(({item}) => [
            _createVNode(_unref(BButton), {
              onClick: _withModifiers(($event: any) => (deleteRecord(item)), ["prevent"]),
              size: "sm",
              variant: "danger"
            }, {
              default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
                _createTextVNode(" Delete ", -1)
              ]))]),
              _: 1
            }, 8, ["onClick"])
          ]),
          "cell.document": _withCtx(({item}) => [
            (!item.file_id)
              ? (_openBlock(), _createBlock(_unref(BButton), {
                  key: 0,
                  onClick: _withModifiers(($event: any) => (uploadDocument(item)), ["prevent"]),
                  size: "sm",
                  variant: "secondary"
                }, {
                  default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                    _createTextVNode(" Upload Document ", -1)
                  ]))]),
                  _: 1
                }, 8, ["onClick"]))
              : (_openBlock(), _createElementBlock("div", _hoisted_9, [
                  _cache[26] || (_cache[26] = _createElementVNode("button", {
                    id: "btnGroupDrop1",
                    type: "button",
                    class: "btn btn-secondary btn-small dropdown-toggle",
                    "data-bs-toggle": "dropdown",
                    "data-bs-popper-config": "{\"strategy\":\"fixed\"}",
                    "aria-expanded": "false"
                  }, " Document Actions ", -1)),
                  _createElementVNode("ul", _hoisted_10, [
                    _createElementVNode("li", null, [
                      _createElementVNode("a", {
                        class: "dropdown-item text-decoration-none",
                        href: "javascript:void(0)",
                        onClick: _withModifiers(($event: any) => (viewDocument(item)), ["prevent"])
                      }, "View/Download Document", 8, _hoisted_11)
                    ]),
                    _createElementVNode("li", null, [
                      _createElementVNode("a", {
                        class: "dropdown-item text-decoration-none",
                        href: "javascript:void(0)",
                        onClick: _withModifiers(($event: any) => (uploadDocument(item)), ["prevent"])
                      }, "Replace Document", 8, _hoisted_12)
                    ]),
                    _createElementVNode("li", null, [
                      _createElementVNode("a", {
                        class: "dropdown-item text-decoration-none",
                        href: "javascript:void(0)",
                        onClick: _withModifiers(($event: any) => (deleteDocument(item)), ["prevent"])
                      }, "Remove Document", 8, _hoisted_13)
                    ])
                  ])
                ]))
          ]),
          _: 1
        }, 8, ["items", "columns", "loading"])),
    _createElementVNode("input", {
      ref_key: "fileInputRef",
      ref: fileInputRef,
      type: "file",
      style: {"display":"none"},
      onChange: handleFileSelected
    }, null, 544),
    _createVNode(_unref(SystemAlerts))
  ], 64))
}
}

})